// widget/src/components/ChatWidget.tsx
import React, { useState } from 'react';
import { ErrorBoundary } from './ErrorBoundary';
import { EmailCapture } from './EmailCapture';
import { ChatInterface } from './ChatInterface';
import { VoiceInterface } from './VoiceInterface';
import { useChat } from '../hooks/useChat';
import { useOpenAIVoice } from '../hooks/useOpenAIVoice';

interface ChatWidgetProps {
  supabaseUrl: string;
  supabaseAnonKey: string;
  position?: 'bottom-right' | 'bottom-left';
  primaryColor?: string;
}

type WidgetState = 'closed' | 'email_capture' | 'mode_select' | 'chat' | 'voice';

export const ChatWidget: React.FC<ChatWidgetProps> = ({
  supabaseUrl,
  supabaseAnonKey,
  position = 'bottom-right',
  primaryColor = '#d4a266'
}) => {
  const [state, setState] = useState<WidgetState>('closed');
  const [userEmail, setUserEmail] = useState<string | null>(null);

  const chat = useChat({ supabaseUrl, supabaseAnonKey, userEmail });
  const voice = useOpenAIVoice({
    supabaseUrl,
    supabaseAnonKey,
    userEmail
  });

  const positionStyles = position === 'bottom-right'
    ? { right: '20px', bottom: '20px' }
    : { left: '20px', bottom: '20px' };

  const handleEmailSubmit = (email: string) => {
    setUserEmail(email);
    setState('mode_select');
  };

  const handleModeSelect = (mode: 'chat' | 'voice') => {
    setState(mode);
    // VoiceInterface will handle auto-connect on mount
  };

  const handleClose = () => {
    if (voice.isConnected) {
      voice.disconnect();
    }
    setState('closed');
  };

  const handleBack = () => {
    // Always disconnect/reset voice state when going back to menu
    // This ensures the next session starts fresh (idle) instead of stuck in 'timeout'
    console.log('ChatWidget: Resetting voice state and returning to menu');
    voice.disconnect();
    setState('mode_select');
  };

  // State for bubble
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipText, setTooltipText] = useState('');
  const [isBubbleHovered, setIsBubbleHovered] = useState(false);

  // Effect for tooltip typewriter
  React.useEffect(() => {
    if (state !== 'closed') return; // Don't show tooltip if widget is open

    const showTimer = setTimeout(() => {
      setShowTooltip(true);
    }, 3000);

    return () => clearTimeout(showTimer);
  }, [state]);

  React.useEffect(() => {
    if (!showTooltip || state !== 'closed') return;

    const fullText = "Ask me anything! 💬";
    let index = 0;
    setTooltipText('');

    const typeInterval = setInterval(() => {
      if (index < fullText.length) {
        setTooltipText(fullText.slice(0, index + 1));
        index++;
      } else {
        clearInterval(typeInterval);
        // Hide after 5 seconds, then show again after 8 more seconds
        setTimeout(() => {
          setShowTooltip(false);
          setTimeout(() => setShowTooltip(true), 8000);
        }, 5000);
      }
    }, 50);

    return () => clearInterval(typeInterval);
  }, [showTooltip, state]);

  // CSS for animations
  const animationStyles = `
    @keyframes novaPulse {
      0% { transform: scale(1); opacity: 0.6; }
      100% { transform: scale(1.8); opacity: 0; }
    }
    @keyframes novaGlow {
      0%, 100% { box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.7); }
      50% { box-shadow: 0 0 0 6px rgba(34, 197, 94, 0); }
    }
    @keyframes novaSpin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }
  `;

  // Closed state - just show the bubble
  if (state === 'closed') {
    return (
      <div
        style={{
          position: 'fixed',
          ...positionStyles,
          zIndex: 9998, // One less than widget container
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          flexDirection: position === 'bottom-left' ? 'row-reverse' : 'row'
        }}
      >
        <style>{animationStyles}</style>

        {/* Animated Tooltip */}
        <div
          style={{
            opacity: showTooltip || isBubbleHovered ? 1 : 0,
            transform: showTooltip || isBubbleHovered ? 'translateX(0) scale(1)' : `translateX(${position === 'bottom-left' ? '-10px' : '10px'}) scale(0.9)`,
            transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
            backgroundColor: 'white',
            padding: '10px 16px',
            borderRadius: '20px',
            borderBottomRightRadius: position === 'bottom-left' ? '20px' : '4px',
            borderBottomLeftRadius: position === 'bottom-left' ? '4px' : '20px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            fontSize: '14px',
            fontWeight: '500',
            color: '#333',
            whiteSpace: 'nowrap',
            pointerEvents: 'none',
          }}
        >
          {tooltipText}
          <span style={{ opacity: tooltipText.length < 18 ? 1 : 0, transition: 'opacity 0.1s' }}>|</span>
        </div>

        {/* Main Bubble Button */}
        <button
          onClick={() => setState('email_capture')}
          onMouseEnter={() => setIsBubbleHovered(true)}
          onMouseLeave={() => setIsBubbleHovered(false)}
          style={{
            position: 'relative',
            width: '64px',
            height: '64px',
            borderRadius: '50%',
            backgroundColor: primaryColor,
            border: 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: isBubbleHovered
              ? `0 8px 30px ${primaryColor}60`
              : `0 4px 20px ${primaryColor}40`,
            transform: isBubbleHovered ? 'scale(1.08)' : 'scale(1)',
            transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
            // Reset position styles since wrapper handles it
            top: 'auto', left: 'auto', right: 'auto', bottom: 'auto'
          }}
          aria-label="Chat with Nova"
        >
          {/* Pulse ring 1 */}
          <div
            style={{
              position: 'absolute',
              width: '100%',
              height: '100%',
              borderRadius: '50%',
              border: `2px solid ${primaryColor}`,
              animation: 'novaPulse 2s ease-out infinite',
            }}
          />
          {/* Pulse ring 2 (delayed) */}
          <div
            style={{
              position: 'absolute',
              width: '100%',
              height: '100%',
              borderRadius: '50%',
              border: `2px solid ${primaryColor}`,
              animation: 'novaPulse 2s ease-out infinite 0.5s',
            }}
          />

          {/* Chat icon */}
          <svg
            width="28"
            height="28"
            viewBox="0 0 24 24"
            fill="none"
            stroke="white"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ position: 'relative', zIndex: 1 }}
          >
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2-2z" />
          </svg>

          {/* Green "online" notification dot */}
          <div
            style={{
              position: 'absolute',
              top: '2px',
              right: '2px',
              width: '14px',
              height: '14px',
              borderRadius: '50%',
              backgroundColor: '#22c55e',
              border: '2px solid white',
              animation: 'novaGlow 1.5s ease-in-out infinite',
            }}
          />
        </button>
      </div>
    );
  }

  // Widget container
  return (
    <div
      style={{
        position: 'fixed',
        ...positionStyles,
        width: '380px',
        height: state === 'email_capture' ? 'auto' : '600px',
        maxHeight: '80vh',
        backgroundColor: 'white',
        borderRadius: '16px',
        boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        zIndex: 9999,
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: '16px 20px',
          backgroundColor: primaryColor,
          color: '#1a1a1a',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {state !== 'email_capture' && state !== 'mode_select' && (
            <button
              onClick={handleBack}
              style={{
                background: 'none',
                border: 'none',
                color: '#1a1a1a',
                cursor: 'pointer',
                padding: '4px',
                display: 'flex',
                alignItems: 'center',
              }}
              aria-label="Back"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M19 12H5M12 19l-7-7 7-7" />
              </svg>
            </button>
          )}
          <div>
            <div style={{ fontWeight: '600', fontSize: '16px' }}>Nova AI Assistant</div>
            <div style={{ fontSize: '12px', opacity: 0.8 }}>
              {state === 'voice' && voice.isConnected ? '🟢 Connected' : 'for Dr. TJ Ahn'}
            </div>
          </div>
        </div>
        <button
          onClick={handleClose}
          style={{
            background: 'none',
            border: 'none',
            color: '#1a1a1a',
            cursor: 'pointer',
            padding: '4px',
          }}
          aria-label="Close"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {state === 'email_capture' && (
          <EmailCapture onSubmit={handleEmailSubmit} primaryColor={primaryColor} />
        )}

        {state === 'mode_select' && (
          <ModeSelect
            onSelect={handleModeSelect}
            userEmail={userEmail || 'Guest'}
            primaryColor={primaryColor}
          />
        )}

        {state === 'chat' && (
          <ChatInterface
            chat={chat}
            primaryColor={primaryColor}
          />
        )}

        {state === 'voice' && (
          <ErrorBoundary>
            <VoiceInterface
              voice={voice}
              primaryColor={primaryColor}
              onBack={handleBack}
              supabaseUrl={supabaseUrl}
              supabaseAnonKey={supabaseAnonKey}
              userEmail={userEmail}
            />
          </ErrorBoundary>
        )}
      </div>
    </div>
  );
};

// Mode selection component
const ModeSelect: React.FC<{
  onSelect: (mode: 'chat' | 'voice') => void;
  userEmail: string | null;
  primaryColor: string;
}> = ({ onSelect, userEmail, primaryColor }) => {
  return (
    <div style={{ padding: '24px', textAlign: 'center' }}>
      <div style={{ marginBottom: '8px', fontSize: '14px', color: '#666' }}>
        Hi! I'm Nova, here to help.
      </div>
      <div style={{ marginBottom: '24px', fontSize: '18px', fontWeight: '500' }}>
        How would you like to connect?
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <button
          onClick={() => onSelect('voice')}
          style={{
            padding: '16px 24px',
            backgroundColor: primaryColor,
            color: '#1a1a1a',
            border: 'none',
            borderRadius: '12px',
            cursor: 'pointer',
            fontSize: '16px',
            fontWeight: '500',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '10px',
            transition: 'opacity 0.2s',
          }}
          onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
          onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
            <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
            <line x1="12" y1="19" x2="12" y2="23" />
            <line x1="8" y1="23" x2="16" y2="23" />
          </svg>
          Talk with Voice
        </button>

        <button
          onClick={() => onSelect('chat')}
          style={{
            padding: '16px 24px',
            backgroundColor: 'white',
            color: '#1a1a1a',
            border: `2px solid ${primaryColor}`,
            borderRadius: '12px',
            cursor: 'pointer',
            fontSize: '16px',
            fontWeight: '500',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '10px',
            transition: 'background-color 0.2s',
          }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#faf6f1'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
          Type a Message
        </button>
      </div>

      <div style={{ marginTop: '20px', fontSize: '12px', color: '#999' }}>
        Connected as {userEmail || 'Guest'}
      </div>
    </div>
  );
};

export default ChatWidget;
